import React, {useEffect, useMemo, useRef, useState} from 'react';
import Modal from '../Modals/Modal';
import ConfirmationModal from '../Modals/ConfirmationModal';
import AddJobModal from './AddJobModal';
import toast from 'react-hot-toast';
import '../Modals/SettingsModal.css';
import './JobManagment.css';

const JobManagment = ({ defWorkId, updateProjectsAndJobs, defaultworkspace }) => {
    // States for job management (active jobs)
    const [selectedJob, setSelectedJob] = useState('');
    const [newJobName, setNewJobName] = useState('');
    const [user, setUser] = useState('');
    const [userRole, setUserRole] = useState('');
    const [allProjects, setAllProjects] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [archivedJobs, setArchivedJobs] = useState([]);
    const [pageLoading, setPageLoading] = useState(true);
    const [archivedJobsLoading, setArchivedJobsLoading] = useState(false);
    const [showArchived, setShowArchived] = useState(false);
    const [jobSearchTerm, setJobSearchTerm] = useState('');
    const [archivedSearchTerm, setArchivedSearchTerm] = useState('');
    
    // Edit job modal states
    const [isEditJobOpen, setIsEditJobOpen] = useState(false);
    const [editJobData, setEditJobData] = useState({
        name: '',
        description: '',
        status: 1,
        type: '',
        priority: null,
        startDate: '',
        endDate: '',
        actualEndDate: '',
        managerId: null,
        projectId: ''
    });
    const [editErrorMessages, setEditErrorMessages] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [usersLoading, setUsersLoading] = useState(false);
    const [workspaceUsers, setWorkspaceUsers] = useState([]);
    const [editLoading, setEditLoading] = useState(false);
    
    // Add job modal visibility
    const [isAddJobOpen, setIsAddJobOpen] = useState(false);
    
    // Activate confirmation modal
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [jobToActivate, setJobToActivate] = useState(null);
    const [activatingJobId, setActivatingJobId] = useState(null);
    
    // Delete confirmation modal
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingJob, setDeletingJob] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    
    // Misc refs
    const clickTimerRef = useRef(null);
    const editErrorRef = useRef(null);
    const editTopRef = useRef(null);
    const editModalContentRef = useRef(null);
    
    // Get current user from localStorage
    useEffect(() => {
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        setUser(storedUser?.id || '');
        setUserRole((storedUser?.role || '').toLowerCase());
    }, []);
    
    // Fetch initial data when workspace changes
    useEffect(() => {
        if (defWorkId && user) {
            fetchInitialData();
        }
    }, [defWorkId, user]);
    
    // Fetch archived jobs when switching to archived view
    useEffect(() => {
        if (showArchived) {
            fetchArchivedJobs();
        }
    }, [showArchived, user]);
    
    // Load workspace users when edit modal opens
    useEffect(() => {
        if (isEditJobOpen) {
            setUsersLoading(true);
            loadWorkspaceUsers();
            setEditErrorMessages([]);
        }
    }, [isEditJobOpen]);
    
    // Fetch job data when edit modal opens and a job is selected
    useEffect(() => {
        if (isEditJobOpen && selectedJob) {
            setEditLoading(true);
            fetchJobData();
        }
    }, [isEditJobOpen, selectedJob]);
    
    // Scroll to top when adding/editing
    useEffect(() => {
        if (isEditing && editModalContentRef.current) {
            try { editModalContentRef.current.scrollTop = 0; } catch(e) {}
            try {
                const container = editModalContentRef.current.closest('.modal-container');
                if (container) container.scrollTop = 0;
            } catch(e) {}
        }
    }, [isEditing]);
    
    useEffect(() => {
        if (editErrorMessages.length > 0 && editErrorRef.current) {
            editErrorRef.current.scrollIntoView({ behavior: 'auto' });
        }
    }, [editErrorMessages]);
    
    // Cleanup click timer
    useEffect(() => {
        return () => {
            if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
        };
    }, []);
    
    // Keyboard shortcut Ctrl+S for edit modal
    const handleEditKeyDown = (event) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 's') {
            event.preventDefault();
            if (isEditJobOpen && !isEditing && editJobData.name.trim()) {
                handleEditJob();
            }
        }
    };
    
    useEffect(() => {
        document.addEventListener('keydown', handleEditKeyDown);
        return () => document.removeEventListener('keydown', handleEditKeyDown);
    }, [isEditJobOpen, isEditing, editJobData.name]);
    
    // API calls
    const fetchInitialData = async () => {
        setPageLoading(true);
        try {
            // Fetch projects that user has access to in this workspace
            const projectsRes = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/Project/GetProjectsByUserJobPermission/${user}/${defWorkId}`
            );
            if (!projectsRes.ok) throw new Error('Failed to fetch projects');
            const projectsData = await projectsRes.json();
            const projects = projectsData.projects || [];
            setAllProjects(projects);
            
            // For each project, fetch jobs that user has access to
            const jobsPromises = projects.map(async (project) => {
                try {
                    const jobsRes = await fetch(
                        `${process.env.REACT_APP_API_BASE_URL}/api/UserJobAuth/GetJobsByUserAndProject/${user}/${project.id}`
                    );
                    if (jobsRes.ok) {
                        const jobsData = await jobsRes.json();
                        return { projectId: project.id, jobs: jobsData.jobs || [] };
                    }
                    return { projectId: project.id, jobs: [] };
                } catch {
                    return { projectId: project.id, jobs: [] };
                }
            });
            const jobsResults = await Promise.all(jobsPromises);
            const allJobs = [];
            jobsResults.forEach(result => {
                result.jobs.forEach(job => {
                    allJobs.push({
                        id: job.jobId,
                        name: job.jobName,
                        projectId: result.projectId,
                        friendlyId: job.friendlyId,
                        status: 1
                    });
                });
            });
            setJobs(allJobs);
        } catch (error) {
            toast.error('Failed to load data');
        } finally {
            setPageLoading(false);
        }
    };
    
    const fetchArchivedJobs = async () => {
        if (!user) return;
        setArchivedJobsLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/Job/GetMyJobsByStatus?userId=${user}&status=3`
            );
            if (response.ok) {
                const data = await response.json();
                const enhancedJobs = data.jobs.map(job => {
                    const project = allProjects.find(p => p.id === job.projectId);
                    return {
                        ...job,
                        projectName: project?.name || 'Unknown Project',
                        workspaceName: defaultworkspace,
                        friendlyId: job.friendlyId
                    };
                });
                setArchivedJobs(enhancedJobs);
            } else {
                toast.error('Failed to load archived jobs');
            }
        } catch (error) {
            toast.error('Network error while loading archived jobs');
        } finally {
            setArchivedJobsLoading(false);
        }
    };
    
    const loadWorkspaceUsers = async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/UserWorkspace/GetUsersByWorkspaceId/${defWorkId}`);
            if (res.ok) {
                const data = await res.json();
                setWorkspaceUsers(data.users || []);
            } else {
                throw new Error();
            }
        } catch {
            toast.error('Failed to load users');
        } finally {
            setUsersLoading(false);
        }
    };
    
    const fetchJobData = async () => {
        setEditLoading(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/Job/GetJobById/${selectedJob}`);
            if (!res.ok) throw new Error();
            const data = await res.json();
            const job = data.job;
            setEditJobData({
                name: job.name || '',
                description: job.description || '',
                status: job.status || 1,
                type: job.type || '',
                priority: job.jobPriority ?? null,
                startDate: job.startDate ? new Date(job.startDate).toISOString().split('T')[0] : '',
                endDate: job.endDate ? new Date(job.endDate).toISOString().split('T')[0] : '',
                actualEndDate: job.actualEndDate ? new Date(job.actualEndDate).toISOString().split('T')[0] : '',
                managerId: job.managerId ?? null,
                projectId: job.projectId || ''
            });
        } catch {
            toast.error('Failed to load job data');
        } finally {
            setEditLoading(false);
        }
    };
    
    const handleEditJob = async () => {
        if (!isEditing) {
            setIsEditing(true);
            if (editTopRef.current) {
                editTopRef.current.scrollIntoView({ behavior: 'auto', block: 'start' });
            }
        }
        setEditErrorMessages([]);
        try {
            const bodyData = {
                id: selectedJob,
                name: editJobData.name.trim(),
                description: editJobData.description.trim(),
                status: editJobData.status,
                type: editJobData.type.trim() || null,
                jobPriority: editJobData.priority,
                startDate: editJobData.startDate ? new Date(editJobData.startDate).toISOString() : null,
                endDate: editJobData.endDate ? new Date(editJobData.endDate).toISOString() : null,
                actualEndDate: editJobData.actualEndDate ? new Date(editJobData.actualEndDate).toISOString() : null,
                managerId: editJobData.managerId
            };
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/Job/UpdateJob/${selectedJob}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bodyData)
            });
            if (!response.ok) {
                const errorData = await response.json();
                let errors = [];
                if (Array.isArray(errorData.errors)) errors = errorData.errors;
                else if (errorData.errors && typeof errorData.errors === 'object') {
                    Object.values(errorData.errors).flat().forEach(msg => errors.push(msg));
                } else if (errorData.message) errors = [errorData.message];
                setEditErrorMessages(errors);
                if (editErrorRef.current) editErrorRef.current.scrollIntoView({ behavior: 'auto' });
                return;
            }
            toast.success('Job updated');
            await fetchInitialData();
            if (showArchived) await fetchArchivedJobs();
            closeEditModal();
        } catch {
            toast.error('Network error');
        } finally {
            setIsEditing(false);
        }
    };
    
    const handleDeleteJob = async () => {
        if (!selectedJob) {
            toast.error("No job selected to delete");
            return;
        }
        setDeletingJob(true);
        setDeleteError(null);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/Job/DeleteJob/${selectedJob}`, {
                method: "DELETE",
                headers: { "Content-Type": "application/json" },
            });
            if (!response.ok) {
                let errorMsg = "";
                if (response.status === 500) {
                    errorMsg = "Unable to delete job. This job may contain notes or be referenced elsewhere.";
                } else if (response.status === 404) {
                    errorMsg = "Job not found. It may have already been deleted.";
                } else if (response.status === 409) {
                    errorMsg = "Cannot delete job. It is being used by other records.";
                } else {
                    errorMsg = `Failed to delete job: ${response.status}`;
                }
                throw new Error(errorMsg);
            }
            toast.success("Job deleted successfully");
            setShowDeleteConfirm(false);
            closeEditModal();
            await fetchInitialData();
            if (showArchived) await fetchArchivedJobs();
        } catch (error) {
            setDeleteError(error.message);
        } finally {
            setDeletingJob(false);
        }
    };
    
    const handleActivateClick = (job) => {
        setJobToActivate(job);
        setIsConfirmModalOpen(true);
    };
    
    const handleConfirmActivate = async () => {
        if (!jobToActivate) return;
        setIsConfirmModalOpen(false);
        setActivatingJobId(jobToActivate.id);
        try {
            const updatePayload = {
                name: jobToActivate.name,
                description: jobToActivate.description || '',
                status: 1,
                type: jobToActivate.type || null,
                jobPriority: jobToActivate.jobPriority !== null ? jobToActivate.jobPriority : null,
                startDate: jobToActivate.startDate || null,
                endDate: jobToActivate.endDate || null,
                actualEndDate: jobToActivate.actualEndDate || null,
                managerId: jobToActivate.managerId !== null ? jobToActivate.managerId : null,
                createdDate: jobToActivate.createdDate || new Date().toISOString()
            };
            const response = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/Job/UpdateJob/${jobToActivate.id}`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(updatePayload)
                }
            );
            if (!response.ok) throw new Error('Failed to activate job');
            toast.success('Job activated successfully');
            await fetchInitialData();
            await fetchArchivedJobs();
        } catch {
            toast.error('Failed to activate job');
        } finally {
            setActivatingJobId(null);
            setJobToActivate(null);
        }
    };
    
    const handleCancelActivate = () => {
        setIsConfirmModalOpen(false);
        setJobToActivate(null);
    };
    
    // Click and double click handlers for active jobs
    const handleJobClick = (jobId) => {
        if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
        clickTimerRef.current = setTimeout(() => {
            setSelectedJob(jobId);
        }, 300);
    };
    
    const handleJobDoubleClick = (jobId) => {
        if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
        setSelectedJob(jobId);
        setIsEditJobOpen(true);
    };
    
    const closeEditModal = () => {
        setIsEditJobOpen(false);
        setEditJobData({
            name: '', description: '', status: 1, type: '', priority: null,
            startDate: '', endDate: '', actualEndDate: '', managerId: null, projectId: ''
        });
        setEditErrorMessages([]);
        setSelectedJob('');
    };
    
    const onJobAdded = () => {
        fetchInitialData();
        if (showArchived) fetchArchivedJobs();
    };
    
    // Memoized filtered jobs for active view
    const filteredJobs = useMemo(() => {
        const searchLower = jobSearchTerm.toLowerCase().trim();
        return jobs
            .filter(job => {
                const project = allProjects.find(p => p.id === job.projectId);
                if (!project) return false;
                return job.name.toLowerCase().includes(searchLower) ||
                       project.name.toLowerCase().includes(searchLower) ||
                       defaultworkspace.toLowerCase().includes(searchLower);
            })
            .map(job => {
                const project = allProjects.find(p => p.id === job.projectId);
                return {
                    ...job,
                    projectName: project?.name || 'Unknown Project',
                    workspaceName: defaultworkspace
                };
            });
    }, [jobs, allProjects, jobSearchTerm, defaultworkspace]);
    
    // Memoized filtered archived jobs
    const filteredArchivedJobs = useMemo(() => {
        const searchLower = archivedSearchTerm.toLowerCase().trim();
        return archivedJobs.filter(job =>
            job.name.toLowerCase().includes(searchLower) ||
            (job.projectName && job.projectName.toLowerCase().includes(searchLower)) ||
            defaultworkspace.toLowerCase().includes(searchLower)
        );
    }, [archivedJobs, archivedSearchTerm, defaultworkspace]);
    
    return (
        <div className="settings-content" style={{ position: 'relative', minHeight: '400px' }}>
            {/* Confirmation Modal for activation */}
            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={handleCancelActivate}
                onConfirm={handleConfirmActivate}
                title="Activate Job"
                message={`Are you sure you want to activate "${jobToActivate?.name}"?`}
                confirmText="Activate"
                cancelText="Cancel"
            />
            
            {pageLoading && (
                <div className="full-overlay">
                    <div className="spinner" />
                    <p style={{ fontSize: '1.2rem', fontWeight: '600', margin: 0 }}>Loading...</p>
                </div>
            )}
            
            {!pageLoading && (
                <>
                    <div className="settings-action-buttons">
                        <button className="btn-primary" onClick={() => setIsAddJobOpen(true)}>
                            Add Job
                        </button>
                        <button
                            className={`btn ${showArchived ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setShowArchived(!showArchived)}
                        >
                            {showArchived ? 'Show Active Jobs' : 'Archived Jobs'}
                        </button>
                    </div>
                    
                    {!showArchived ? (
                        /* Active Jobs View */
                        <div className="settings-lookup-list">
                            <h4>Job Lookups</h4>
                            {jobs.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '50px', color: '#888' }}>
                                    No jobs found
                                </div>
                            ) : (
                                <div className="search-wrapper">
                                    <input
                                        type="text"
                                        value={jobSearchTerm}
                                        onChange={(e) => setJobSearchTerm(e.target.value)}
                                        placeholder="Search by workspace, project, or job name..."
                                        style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
                                    />
                                    {jobSearchTerm.trim() && filteredJobs.length === 0 && (
                                        <div className="dropdown-message error-box">No jobs match your search</div>
                                    )}
                                    {!jobSearchTerm.trim() && filteredJobs.length === 0 && (
                                        <div className="error-box">No jobs available</div>
                                    )}
                                    {filteredJobs.length > 0 && (
                                        <ul className="project-search-list">
                                            {filteredJobs.map(job => (
                                                <li
                                                    key={job.id}
                                                    className={`double-clickable ${selectedJob === job.id ? 'selected' : ''}`}
                                                    onClick={() => handleJobClick(job.id)}
                                                    onDoubleClick={() => handleJobDoubleClick(job.id)}
                                                    style={{ cursor: 'pointer' }}
                                                    title="Click to select, double-click to edit"
                                                >
                                                    <div className="job-path">
                                                        <span className="workspace-name">{job.workspaceName}</span>
                                                        <span className="path-separator">→</span>
                                                        <span className="workspace-name">{job.projectName}</span>
                                                        <span className="path-separator">→</span>
                                                        <span className="job-name">{job.name}</span>
                                                    </div>
                                                    <span className="job-status-badge status-active">Active</span>
                                                    <button
                                                        className="delete-job-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedJob(job.id);
                                                            setNewJobName(job.name);
                                                            setShowDeleteConfirm(true);
                                                        }}
                                                        title="Delete job"
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Archived Jobs View */
                        <div className="settings-lookup-list">
                            <h4>Archived Jobs</h4>
                            <div className="search-wrapper" style={{ marginBottom: '20px' }}>
                                <input
                                    type="text"
                                    value={archivedSearchTerm}
                                    onChange={(e) => setArchivedSearchTerm(e.target.value)}
                                    placeholder="Search archived jobs..."
                                    style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
                                />
                            </div>
                            {archivedJobsLoading && (
                                <div className="full-overlay" style={{ position: 'relative', minHeight: '200px' }}>
                                    <div className="spinner" style={{ width: '40px', height: '40px', borderWidth: '4px' }} />
                                    <p style={{ fontSize: '1rem', fontWeight: '600', margin: '10px 0 0' }}>Loading archived jobs...</p>
                                </div>
                            )}
                            {!archivedJobsLoading && archivedJobs.length === 0 && (
                                <div style={{ textAlign: 'center', padding: '50px', color: '#888' }}>
                                    No archived jobs found
                                </div>
                            )}
                            {!archivedJobsLoading && filteredArchivedJobs.length === 0 && archivedSearchTerm.trim() && (
                                <div className="error-box" style={{ marginTop: '10px' }}>No archived jobs match your search</div>
                            )}
                            {!archivedJobsLoading && filteredArchivedJobs.length > 0 && (
                                <ul className="project-search-list" style={{ position: 'relative', maxHeight: '400px', overflowY: 'auto' }}>
                                    {filteredArchivedJobs.map(job => (
                                        <li key={job.id} style={{ padding: '12px 15px' }}>
                                            <div className="archived-job-item">
                                                <div className="archived-job-info">
                                                    <span className="archived-job-name">{job.name}</span>
                                                    <span className="archived-job-meta">
                                                        {job.workspaceName} → {job.projectName}
                                                        {job.managerId && ` • Manager ID: ${job.managerId}`}
                                                        {job.jobPriority && ` • Priority: ${job.jobPriority}`}
                                                    </span>
                                                </div>
                                                <button
                                                    className="activate-button"
                                                    onClick={() => handleActivateClick(job)}
                                                    disabled={activatingJobId === job.id}
                                                >
                                                    {activatingJobId === job.id ? 'Activating...' : 'Activate'}
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </>
            )}
            
            {/* Add Job Modal */}
            <AddJobModal
                isOpen={isAddJobOpen}
                onClose={() => setIsAddJobOpen(false)}
                defWorkId={defWorkId}
                user={user}
                onJobAdded={onJobAdded}
                isAdmin={userRole === 'admin'}
            />
            
            {/* Edit Job Modal */}
            <Modal isOpen={isEditJobOpen} onClose={closeEditModal} title="Edit Job" customClass="modal-sm">
                <div ref={editModalContentRef} className="settings-form" style={{ position: 'relative' }}>
                    <div ref={editTopRef}></div>
                    {(usersLoading || editLoading || isEditing) && (
                        <div className="full-overlay">
                            <div className="spinner" />
                            <p style={{ fontSize: '1.2rem', fontWeight: '600', margin: 0 }}>
                                {usersLoading ? 'Loading users...' : editLoading ? 'Loading job...' : 'Updating job...'}
                            </p>
                        </div>
                    )}
                    <div ref={editErrorRef} style={{ marginBottom: '15px', gridColumn: 'span 2' }}>
                        {editErrorMessages.length > 0 && (
                            <div className="error-message">
                                <ul style={{ color: '#d32f2f', margin: '8px 0' }}>
                                    {editErrorMessages.map((msg, i) => <li key={i}>{msg}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                    <div className="form-container">
                        <div className="form-group">
                            <label>Project:</label>
                            <select value={editJobData.projectId} disabled>
                                {allProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Job Name:</label>
                            <input value={editJobData.name} onChange={e => setEditJobData(prev => ({ ...prev, name: e.target.value }))} />
                        </div>
                        <div className="form-group">
                            <label>Status:</label>
                            <select value={editJobData.status} onChange={e => setEditJobData(prev => ({ ...prev, status: parseInt(e.target.value) }))}>
                                <option value={1}>Active</option>
                                <option value={2}>Inactive</option>
                                <option value={3}>Archive</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Type:</label>
                            <input type="text" value={editJobData.type} onChange={e => setEditJobData(prev => ({ ...prev, type: e.target.value }))} placeholder="Enter job type" />
                        </div>
                        <div className="form-group">
                            <label>Priority:</label>
                            <select value={editJobData.priority ?? ''} onChange={e => setEditJobData(prev => ({ ...prev, priority: e.target.value === '' ? null : parseInt(e.target.value) }))}>
                                <option value="">Select Priority</option>
                                <option value={1}>1</option><option value={2}>2</option><option value={3}>3</option>
                                <option value={4}>4</option><option value={5}>5</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label>Start Date:</label>
                            <input type="date" value={editJobData.startDate} onChange={e => setEditJobData(prev => ({ ...prev, startDate: e.target.value }))} />
                        </div>
                        <div className="form-group">
                            <label>End Date:</label>
                            <input type="date" value={editJobData.endDate} onChange={e => setEditJobData(prev => ({ ...prev, endDate: e.target.value }))} />
                        </div>
                        <div className="form-group">
                            <label>Actual End Date:</label>
                            <input type="date" value={editJobData.actualEndDate} onChange={e => setEditJobData(prev => ({ ...prev, actualEndDate: e.target.value }))} />
                        </div>
                        <div className="form-group">
                            <label>Manager:</label>
                            <select value={editJobData.managerId ?? ''} onChange={e => setEditJobData(prev => ({ ...prev, managerId: e.target.value === '' ? null : parseInt(e.target.value) }))}>
                                <option value="">Select Manager</option>
                                {workspaceUsers.map(u => (
                                    <option key={u.userId} value={u.userId}>{`${u.fname} ${u.lname} (${u.email})`}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group full-width">
                            <label>Description:</label>
                            <textarea value={editJobData.description} onChange={e => setEditJobData(prev => ({ ...prev, description: e.target.value }))} />
                        </div>
                    </div>
                    <div className="modal-footer" style={{ marginTop: '30px', gridColumn: 'span 2' }}>
                        <button className="btn-primary" onClick={handleEditJob} disabled={!editJobData.name.trim() || isEditing}>
                            {isEditing ? 'Updating...' : 'OK'}
                        </button>
                        <button className="btn-close" onClick={closeEditModal}>Cancel</button>
                    </div>
                </div>
            </Modal>
            
            {/* Delete Confirmation Modal */}
            <ConfirmationModal
                isOpen={showDeleteConfirm}
                onClose={() => {
                    setShowDeleteConfirm(false);
                    setDeleteError(null);
                }}
                onConfirm={handleDeleteJob}
                title="Delete Job"
                message={
                    <>
                        Are you sure you want to delete <strong>"{newJobName}"</strong>?
                        {deleteError && (
                            <span style={{
                                display: 'block', marginTop: '15px', padding: '10px', backgroundColor: '#fff5f5',
                                border: '1px solid #dc3545', borderRadius: '6px', color: '#dc3545', fontSize: '13px'
                            }}>
                                <i className="fas fa-exclamation-circle" style={{ marginRight: '8px' }}></i>
                                {deleteError}
                            </span>
                        )}
                    </>
                }
                confirmText={deletingJob ? "Deleting..." : "Delete"}
                cancelText="Cancel"
                isDanger={true}
            />
        </div>
    );
};

export default JobManagment;